import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCRM } from '../context/CRMContext';
import { useProjects } from '../context/ProjectContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Communication } from '../types/crm';
import { formatCurrency } from '../utils/calculations';
import { ArrowLeftIcon, PencilIcon, MailIcon, PhoneIcon, MessageSquareIcon, BuildingIcon, ClockIcon, ArrowDownIcon, ArrowUpIcon, FolderIcon, CheckCircleIcon } from 'lucide-react';
type TabType = 'messages' | 'calls' | 'emails' | 'projects';
export function ContactView() {
  const navigate = useNavigate();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const {
    getContactById,
    getCommunicationsByContactId
  } = useCRM();
  const {
    getProjectsByContactId
  } = useProjects();
  const [activeTab, setActiveTab] = useState<TabType>('messages');
  const contact = id ? getContactById(id) : undefined;
  const allCommunications = id ? getCommunicationsByContactId(id) : [];
  const projects = id ? getProjectsByContactId(id) : [];
  if (!contact) {
    return <div className="text-center py-12" data-id="element-719">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-720">
          Contact not found
        </h2>
        <Button onClick={() => navigate('/lead-flow')} data-id="element-721">Back to Contacts</Button>
      </div>;
  }
  const filteredCommunications = allCommunications.filter(comm => comm.type === activeTab.slice(0, -1));
  const getStatusColor = (status: typeof contact.status) => {
    switch (status) {
      case 'lead':
        return 'warning';
      case 'contact':
        return 'info';
      case 'customer':
        return 'success';
      case 'lost':
        return 'default';
      default:
        return 'default';
    }
  };
  const getTotalLifetimePurchase = () => {
    return contact.companies.reduce((sum, rel) => sum + rel.lifetime_purchase, 0);
  };
  const getTotalProjectValue = () => {
    return projects.reduce((sum, project) => sum + project.value, 0);
  };
  const formatTimestamp = (date: Date) => {
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  const getDirectionIcon = (direction: Communication['direction']) => {
    return direction === 'inbound' ? <ArrowDownIcon className="w-4 h-4 text-green-600" data-id="element-722" /> : <ArrowUpIcon className="w-4 h-4 text-blue-600" data-id="element-723" />;
  };
  const getDirectionLabel = (direction: Communication['direction']) => {
    return direction === 'inbound' ? 'Inbound' : 'Outbound';
  };
  return <div className="space-y-6" data-id="element-724">
      {/* Header */}
      <div className="flex items-start justify-between" data-id="element-725">
        <div className="flex items-start gap-4" data-id="element-726">
          <Button variant="ghost" onClick={() => navigate('/lead-flow')} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-728" />} size="sm" data-id="element-727">
            Back
          </Button>
          <div data-id="element-729">
            <div className="flex items-center gap-3 mb-2" data-id="element-730">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold text-lg" data-id="element-731">
                {contact.first_name[0]}
                {contact.last_name[0]}
              </div>
              <div data-id="element-732">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-733">
                  {contact.first_name} {contact.last_name}
                </h1>
                <p className="text-sm text-gray-600" data-id="element-734">{contact.company}</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-735">
              <Badge variant={getStatusColor(contact.status)} data-id="element-736">
                {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
              </Badge>
              {contact.companies.length > 0 && <span className="text-sm text-gray-600" data-id="element-737">
                  {contact.companies.length}{' '}
                  {contact.companies.length === 1 ? 'company' : 'companies'}
                </span>}
            </div>
          </div>
        </div>
        <Button onClick={() => navigate(`/lead-flow/edit/${contact.id}`)} leftIcon={<PencilIcon className="w-4 h-4" data-id="element-739" />} size="sm" data-id="element-738">
          Edit
        </Button>
      </div>

      {/* Contact Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-740">
        <Card data-id="element-741">
          <div className="space-y-3" data-id="element-742">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-743">
              Contact Information
            </h3>
            <div className="space-y-2" data-id="element-744">
              <div className="flex items-center gap-2 text-sm" data-id="element-745">
                <MailIcon className="w-4 h-4 text-gray-400" data-id="element-746" />
                <a href={`mailto:${contact.email_primary}`} className="text-blue-600 hover:text-blue-700" data-id="element-747">
                  {contact.email_primary}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="element-748">
                <PhoneIcon className="w-4 h-4 text-gray-400" data-id="element-749" />
                <a href={`tel:${contact.phone_primary}`} className="text-blue-600 hover:text-blue-700" data-id="element-750">
                  {contact.phone_primary}
                </a>
              </div>
            </div>
          </div>
        </Card>

        <Card data-id="element-751">
          <div className="space-y-3" data-id="element-752">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-753">Companies</h3>
            {contact.companies.length === 0 ? <p className="text-sm text-gray-500" data-id="element-754">No company relationships</p> : <div className="space-y-2" data-id="element-755">
                {contact.companies.map((rel, idx) => <div key={idx} className="flex items-center gap-2 text-sm" data-id="element-756">
                    <BuildingIcon className="w-4 h-4 text-gray-400" data-id="element-757" />
                    <span className="font-medium" data-id="element-758">{rel.company_name}</span>
                    <span className="text-gray-500" data-id="element-759">•</span>
                    <span className="text-gray-600" data-id="element-760">
                      {formatCurrency(rel.lifetime_purchase)}
                    </span>
                  </div>)}
              </div>}
          </div>
        </Card>

        <Card data-id="element-761">
          <div className="space-y-3" data-id="element-762">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-763">
              Lifetime Value
            </h3>
            <div className="text-3xl font-bold text-gray-900" data-id="element-764">
              {formatCurrency(getTotalLifetimePurchase())}
            </div>
            <p className="text-xs text-gray-500" data-id="element-765">Total across all companies</p>
          </div>
        </Card>

        <Card data-id="element-766">
          <div className="space-y-3" data-id="element-767">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-768">
              Project Value
            </h3>
            <div className="text-3xl font-bold text-orange-600" data-id="element-769">
              {formatCurrency(getTotalProjectValue())}
            </div>
            <p className="text-xs text-gray-500" data-id="element-770">
              {projects.length} {projects.length === 1 ? 'project' : 'projects'}
            </p>
          </div>
        </Card>
      </div>

      {/* Communications & Projects Tabs */}
      <Card padding="none" data-id="element-771">
        <div className="border-b border-gray-200" data-id="element-772">
          <div className="flex" data-id="element-773">
            <button onClick={() => setActiveTab('messages')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'messages' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-774">
              <div className="flex items-center gap-2" data-id="element-775">
                <MessageSquareIcon className="w-4 h-4" data-id="element-776" />
                Messages
              </div>
            </button>
            <button onClick={() => setActiveTab('calls')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'calls' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-777">
              <div className="flex items-center gap-2" data-id="element-778">
                <PhoneIcon className="w-4 h-4" data-id="element-779" />
                Calls
              </div>
            </button>
            <button onClick={() => setActiveTab('emails')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'emails' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-780">
              <div className="flex items-center gap-2" data-id="element-781">
                <MailIcon className="w-4 h-4" data-id="element-782" />
                Emails
              </div>
            </button>
            <button onClick={() => setActiveTab('projects')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'projects' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-783">
              <div className="flex items-center gap-2" data-id="element-784">
                <FolderIcon className="w-4 h-4" data-id="element-785" />
                Projects
              </div>
            </button>
          </div>
        </div>

        <div className="p-6" data-id="element-786">
          {activeTab === 'projects' ?
        // Projects Tab
        projects.length === 0 ? <div className="text-center py-8" data-id="element-787">
                <p className="text-sm text-gray-500" data-id="element-788">
                  No projects associated with this contact
                </p>
              </div> : <div className="space-y-4" data-id="element-789">
                {projects.map(project => <div key={project.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => navigate('/projects')} data-id="element-790">
                    <div className="flex-1 min-w-0" data-id="element-791">
                      <div className="flex items-center gap-2 mb-2" data-id="element-792">
                        {project.completed && <CheckCircleIcon className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-793" />}
                        <h4 className={`text-sm font-semibold ${project.completed ? 'text-gray-400 line-through' : 'text-gray-900'}`} data-id="element-794">
                          {project.title}
                        </h4>
                      </div>
                      {project.tasks.length > 0 && <p className="text-xs text-gray-500" data-id="element-795">
                          {project.tasks.filter(t => t.completed).length}/
                          {project.tasks.length} tasks completed
                        </p>}
                    </div>
                    <div className="text-right ml-4" data-id="element-796">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-797">
                        {formatCurrency(project.value)}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-798">
                        {new Date(project.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>)}
              </div> :
        // Communications Tabs
        filteredCommunications.length === 0 ? <div className="text-center py-8" data-id="element-799">
              <p className="text-sm text-gray-500" data-id="element-800">No {activeTab} yet</p>
            </div> : <div className="space-y-4" data-id="element-801">
              {filteredCommunications.map(comm => <div key={comm.id} className="flex gap-4 p-4 bg-gray-50 rounded-lg" data-id="element-802">
                  <div className="flex-shrink-0" data-id="element-803">
                    {getDirectionIcon(comm.direction)}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-804">
                    <div className="flex items-start justify-between mb-2" data-id="element-805">
                      <div data-id="element-806">
                        <span className={`text-xs font-medium ${comm.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'}`} data-id="element-807">
                          {getDirectionLabel(comm.direction)}
                        </span>
                        {comm.subject && <h4 className="text-sm font-semibold text-gray-900 mt-1" data-id="element-808">
                            {comm.subject}
                          </h4>}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-809">
                        <ClockIcon className="w-3 h-3" data-id="element-810" />
                        {formatTimestamp(comm.timestamp)}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap" data-id="element-811">
                      {comm.content}
                    </p>
                    {comm.duration_minutes && <p className="text-xs text-gray-500 mt-2" data-id="element-812">
                        Duration: {comm.duration_minutes} minutes
                      </p>}
                  </div>
                </div>)}
            </div>}
        </div>
      </Card>

      {/* Notes */}
      {contact.notes && <Card data-id="element-813">
          <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-814">Notes</h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap" data-id="element-815">
            {contact.notes}
          </p>
        </Card>}
    </div>;
}